import type { ZudokuConfig } from 'zudoku';

import { ApiReferenceHub } from './src/components/ApiReferenceHub';
import { DocsHome } from './src/components/DocsHome';
import { WhichKey } from './src/components/WhichKey';
import { docsBasePath, docsRoute } from './src/docsPaths';

// The developer console is a separate authenticated origin. Keep the
// docs' "Console" / "Sign in" entry points pointed at it so docs and console
// stay one product.
const CORPORATE_URL = 'https://pokerworks.io';
const CONSOLE_URL = 'https://console.pokerworks.io';
const CONSOLE_SIGNIN_URL = `${CONSOLE_URL}/login`;
const CONSOLE_SIGNUP_URL = `${CONSOLE_URL}/signup`;

const config: ZudokuConfig = {
  basePath: docsBasePath,
  canonicalUrlOrigin: 'https://pokerworks.io',
  site: {
    showPoweredBy: false,
    logo: {
      src: {
        light: 'logo-light.svg',
        dark: 'logo-dark.svg',
      },
      alt: 'PokerWorks',
      width: '150px',
    },
    // No footer — the docs are a focused reference surface; cross-links live in
    // the header and the homepage cards.
  },
  header: {
    placements: {
      search: 'center',
    },
  },
  metadata: {
    title: '%s - PokerWorks Docs',
    description:
      'Developer documentation for PokerWorks APIs, SDKs, calculation engines, and hosted social-poker platform surfaces.',
    favicon: 'favicon.svg',
  },
  navigation: [
    {
      type: 'category',
      label: 'Documentation',
      // The route for a doc is generated from its `file` path (relative to the
      // pages dir, no extension, no leading slash). Home overrides to '/'.
      link: {
        type: 'doc',
        path: '/',
        file: 'home',
      },
      items: [
        { type: 'filter', placeholder: 'Filter documentation' },
        { type: 'doc', file: 'get-started', icon: 'rocket' },
        {
          // Concepts = EXPLANATION only (Diátaxis). Look-up reference pages
          // (errors, rate limits) live under "Reference & support".
          type: 'category',
          label: 'Concepts',
          icon: 'lightbulb',
          collapsible: true,
          collapsed: false,
          items: [
            {
              type: 'doc',
              file: 'concepts/choose-your-api-path',
              label: 'Choose your API path',
              icon: 'route',
            },
            {
              type: 'doc',
              file: 'concepts/first-api-call-and-collections',
              label: 'First API call & collections',
              icon: 'terminal',
            },
            { type: 'doc', file: 'concepts/how-the-api-works', icon: 'compass' },
            {
              type: 'doc',
              file: 'concepts/authentication',
              label: 'Authentication & API keys',
              icon: 'lock',
            },
            {
              type: 'doc',
              file: 'concepts/public-data-use-and-brand-rules',
              label: 'Public data & brand rules',
              icon: 'shield',
            },
            {
              type: 'doc',
              file: 'concepts/public-read-cache-and-widget-freshness',
              label: 'Public read freshness',
              icon: 'activity',
            },
            {
              type: 'doc',
              file: 'concepts/integrity-tiers',
              label: 'Integrity tiers',
              icon: 'shield-check',
            },
            {
              type: 'doc',
              file: 'concepts/approved-integrations',
              label: 'Approved integrations',
              icon: 'badge-check',
            },
            {
              type: 'doc',
              file: 'concepts/developer-ecosystem-programs',
              label: 'Developer ecosystem programs',
              icon: 'users',
            },
            { type: 'doc', file: 'concepts/usage-and-credits', icon: 'wallet' },
          ],
        },
        {
          // MANAGEMENT API plane: pw_mgmt_ service-account tokens. Show the
          // beta HTTP path in primary nav; private repo-owned CLI, MCP,
          // and management-client pages stay unlisted until public artifacts
          // exist.
          type: 'category',
          label: 'Automation & management',
          icon: 'workflow',
          collapsible: true,
          collapsed: false,
          items: [
            {
              type: 'doc',
              file: 'automation/rooms',
              label: 'Run a PokerDeck room',
              icon: 'landmark',
            },
            {
              type: 'doc',
              file: 'automation/automate-with-a-service-account',
              label: 'Guide: Automate with a service account',
              icon: 'route',
            },
            {
              type: 'doc',
              file: 'automation/service-accounts',
              label: 'Service accounts & management tokens',
              icon: 'key-round',
            },
            { type: 'doc', file: 'automation/webhooks', label: 'Webhooks', icon: 'send' },
            {
              type: 'doc',
              file: 'automation/tournament-results',
              label: 'Tournament results & verification',
              icon: 'file-check',
            },
            {
              type: 'link',
              label: 'Management API Reference',
              to: docsRoute('/api/management'),
              icon: 'book-open',
            },
          ],
        },
        {
          // CALCULATION API plane: pw_test_ / pw_live_ keys. It remains the
          // fast first-call on-ramp, not the primary platform control plane.
          type: 'category',
          label: 'Calculation API',
          icon: 'calculator',
          collapsible: true,
          collapsed: false,
          items: [
            { type: 'doc', file: 'calculations/overview', label: 'Overview', icon: 'list' },
            { type: 'doc', file: 'calculations/holdem-equity', icon: 'spade' },
            { type: 'doc', file: 'calculations/icm-deals', icon: 'handshake' },
            { type: 'doc', file: 'calculations/tournament-payouts', icon: 'trophy' },
            { type: 'doc', file: 'calculations/blind-structures', icon: 'layers' },
            { type: 'doc', file: 'calculations/chip-starting-stacks', icon: 'coins' },
            { type: 'doc', file: 'sdk', label: 'TypeScript SDK', icon: 'package' },
            {
              type: 'link',
              label: 'Calculation API Reference',
              to: docsRoute('/api/calculations'),
              icon: 'book-open',
            },
          ],
        },
        {
          // Recipes are outcome-led maintained examples. Keep them separate
          // from endpoint reference so builders see what to build before they
          // dive into individual contracts.
          type: 'category',
          label: 'Recipes',
          icon: 'route',
          collapsible: true,
          collapsed: false,
          items: [
            { type: 'doc', file: 'recipes/overview', label: 'Overview', icon: 'list' },
            {
              type: 'doc',
              file: 'recipes/demo-room-provisioning',
              label: 'Demo room provisioning',
              icon: 'route',
            },
            {
              type: 'doc',
              file: 'recipes/embed-club-lobby',
              label: 'Embed a club lobby',
              icon: 'monitor-play',
            },
            {
              type: 'doc',
              file: 'recipes/result-proof-explorer',
              label: 'Tournament result explorer',
              icon: 'file-check',
            },
            {
              type: 'doc',
              file: 'recipes/discord-tournament-announcements',
              label: 'Discord tournament announcements',
              icon: 'message-circle',
            },
            {
              type: 'doc',
              file: 'recipes/google-sheets-results',
              label: 'Google Sheets results',
              icon: 'sheet',
            },
            {
              type: 'doc',
              file: 'recipes/streamer-overlay',
              label: 'Streamer overlay',
              icon: 'activity',
            },
          ],
        },
        {
          type: 'doc',
          file: 'versioning-and-stability',
          label: 'Versioning & stability',
          icon: 'git-branch',
        },
        {
          // REFERENCE + meta (look-up + housekeeping).
          type: 'category',
          label: 'Reference & support',
          icon: 'book-open',
          collapsible: true,
          collapsed: false,
          items: [
            {
              type: 'doc',
              file: 'reference/errors',
              label: 'Errors & troubleshooting',
              icon: 'alert-triangle',
            },
            { type: 'doc', file: 'reference/rate-limits', icon: 'gauge' },
            {
              type: 'doc',
              file: 'reference/access',
              label: 'Access & limits',
              icon: 'shield-check',
            },
            { type: 'doc', file: 'security', icon: 'shield' },
            { type: 'doc', file: 'reference/status', label: 'Status', icon: 'activity' },
            { type: 'doc', file: 'reference/glossary', label: 'Glossary', icon: 'book-open' },
            { type: 'doc', file: 'support', icon: 'life-buoy' },
            { type: 'doc', file: 'changelog', icon: 'history' },
          ],
        },
      ],
    },
    {
      type: 'link',
      label: 'API Reference',
      to: docsRoute('/api'),
    },
    // Right side of the header: a link back to the marketing site plus the
    // console entry points. These are STATIC for now — the docs are a separate
    // origin from the console (which owns auth), so we can't yet detect login
    // state here. A returning, already-signed-in user who clicks "Sign in" is
    // sent straight to their dashboard by the console.
    //
    // Auth-aware upgrade (pending the platform auth contract): once Zudoku's
    // `authentication` is wired to a shared identity provider, switch these to
    // `display: 'anon'` and add a `display: 'auth'` "Dashboard" link so the
    // header shows Dashboard when logged in vs Sign in / Get started when not.
    {
      type: 'link',
      label: 'pokerworks.io',
      to: CORPORATE_URL,
      icon: 'arrow-up-right',
    },
    {
      type: 'link',
      label: 'Sign in',
      to: CONSOLE_SIGNIN_URL,
    },
    {
      type: 'link',
      label: 'Get started',
      to: CONSOLE_SIGNUP_URL,
    },
  ],
  apis: [
    {
      type: 'file',
      input: './apis/pokerworks-calculation-api.openapi.json',
      path: docsRoute('/api/calculations'),
      options: {
        expandAllTags: true,
      },
    },
    {
      type: 'file',
      input: './apis/pokerworks-management-api.openapi.json',
      path: docsRoute('/api/management'),
      options: {
        expandAllTags: true,
      },
    },
  ],
  // Keep the pre-restructure URLs alive: errors, rate limits, and access moved
  // out of /concepts (and top-level) into /reference to match the IA grouping.
  redirects: [
    { from: docsRoute('/concepts/errors'), to: docsRoute('/reference/errors') },
    { from: docsRoute('/concepts/rate-limits'), to: docsRoute('/reference/rate-limits') },
    { from: docsRoute('/access'), to: docsRoute('/reference/access') },
  ],
  docs: {
    files: '/pages/**/*.{md,mdx}',
    defaultOptions: {
      showLastModified: false,
    },
  },
  mdx: {
    components: {
      ApiReferenceHub,
      DocsHome,
      WhichKey,
    },
  },
  search: {
    type: 'pagefind',
  },
  aiAssistants: ['chatgpt', 'claude'],
  theme: {
    // Match the console: Hanken Grotesk body + JetBrains Mono code.
    fonts: {
      sans: {
        url: 'https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700&display=swap',
        fontFamily: "'Hanken Grotesk', ui-sans-serif, system-ui, sans-serif",
      },
      mono: 'JetBrains Mono',
    },
    light: {
      background: '40 33% 98%',
      foreground: '27 32% 11%',
      primary: '40 63% 47%',
      primaryForeground: '0 0% 100%',
      muted: '38 30% 96%',
      mutedForeground: '27 18% 36%',
      border: '36 17% 88%',
      card: '0 0% 100%',
      cardForeground: '27 32% 11%',
      // Hover surface — a touch darker than the near-white page so hover reads.
      accent: '38 28% 93%',
      accentForeground: '27 32% 11%',
    },
    dark: {
      background: '28 27% 9%',
      foreground: '38 52% 93%',
      primary: '40 63% 58%',
      primaryForeground: '27 32% 11%',
      muted: '27 23% 16%',
      mutedForeground: '38 24% 72%',
      border: '29 21% 25%',
      card: '28 25% 12%',
      cardForeground: '38 52% 93%',
      accent: '27 22% 20%',
      accentForeground: '38 52% 97%',
    },
    customCss: `
      :root {
        --pw-docs-ink: #241b14;
        --pw-docs-muted: #6f6258;
        --pw-docs-line: #e8e2d7;
        --pw-docs-paper-soft: #fbfaf7;
        --pw-docs-gold: #b7812f;
        --pw-docs-green: #315f48;
      }

      body {
        background: #fbfaf7;
      }

      .dark body {
        background: #161310;
      }

      /* Header "Get started" is the primary CTA — render it as a gold button.
         Scoped to the header so in-content "sign up" links aren't affected. */
      header a[href="${CONSOLE_SIGNUP_URL}"] {
        align-self: center;
        border-radius: 8px;
        background: hsl(40 63% 47%);
        color: #fff;
        padding: 0.34rem 0.78rem;
        font-weight: 600;
      }

      header a[href="${CONSOLE_SIGNUP_URL}"]:hover {
        background: hsl(40 63% 42%);
        color: #fff;
      }

      .pw-docs-home {
        max-width: 920px;
      }

      body:has(.pw-docs-home) .prose > .flow-root:first-child {
        display: none;
      }

      .pw-docs-eyebrow {
        margin: 0 0 12px;
        color: var(--pw-docs-gold);
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .pw-docs-home h1 {
        margin: 0;
        color: var(--pw-docs-ink);
        font-size: clamp(1.9rem, 2.6vw, 2.3rem);
        line-height: 1.12;
        letter-spacing: -0.01em;
      }

      .dark .pw-docs-home h1 {
        color: #f5ead8;
      }

      .pw-docs-lede {
        max-width: 700px;
        margin: 18px 0 32px;
        color: var(--pw-docs-muted);
        font-size: 1.05rem;
        line-height: 1.65;
      }

      .dark .pw-docs-lede {
        color: #d8cab5;
      }

      .pw-docs-feature-grid,
      .pw-docs-card-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
        margin: 16px 0 38px;
      }

      .pw-docs-feature-card,
      .pw-docs-card {
        display: flex;
        min-height: 118px;
        align-items: flex-start;
        flex-direction: row;
        gap: 16px;
        border: 1px solid var(--pw-docs-line);
        border-radius: 12px;
        background: #ffffff;
        color: var(--pw-docs-ink);
        padding: 20px;
        text-decoration: none;
        transition:
          border-color 160ms ease,
          box-shadow 160ms ease,
          transform 160ms ease;
      }

      .pw-docs-feature-card {
        min-height: 104px;
        background: #ffffff;
      }

      .pw-docs-feature-card:hover,
      .pw-docs-card:hover {
        border-color: color-mix(in srgb, var(--pw-docs-gold) 44%, var(--pw-docs-line));
        box-shadow: 0 14px 34px rgb(36 27 20 / 0.08);
        transform: translateY(-1px);
      }

      .pw-docs-feature-card span,
      .pw-docs-card span {
        font-size: 0.98rem;
        font-weight: 600;
      }

      .pw-docs-feature-card small,
      .pw-docs-card small {
        /* Regular weight so descriptions read as secondary text, not emphasised
           (the parent span sets 600, which <small> would otherwise inherit). */
        color: var(--pw-docs-muted);
        font-size: 0.9rem;
        font-weight: 400;
        line-height: 1.55;
      }

      .pw-docs-card-copy {
        display: flex;
        min-width: 0;
        flex-direction: column;
        gap: 8px;
      }

      .pw-docs-card-copy strong {
        color: var(--pw-docs-ink);
        font-size: 0.98rem;
        font-weight: 600;
      }

      .pw-docs-icon,
      .pw-docs-link-icon {
        display: inline-flex;
        width: 42px;
        height: 42px;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
      }

      .pw-docs-link-icon {
        width: 22px;
        height: 22px;
        color: #a9b0bc;
      }

      .pw-docs-icon svg,
      .pw-docs-link-icon svg {
        width: 20px;
        height: 20px;
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 1.9;
      }

      .pw-docs-icon-blue {
        background: #edf4ff;
        color: #315fbe;
      }

      .pw-docs-icon-purple {
        background: #f5efff;
        color: #8a3ffc;
      }

      .pw-docs-icon-green {
        background: #edf9f2;
        color: #16825a;
      }

      .pw-docs-icon-gold {
        background: #fff6e6;
        color: #c47a13;
      }


      .dark .pw-docs-feature-card,
      .dark .pw-docs-card {
        border-color: #3b3027;
        background: #211914;
        color: #f5ead8;
      }

      .dark .pw-docs-feature-card small,
      .dark .pw-docs-card small {
        color: #cbbca7;
      }

      @media (max-width: 760px) {
        .pw-docs-feature-grid,
        .pw-docs-card-grid {
          grid-template-columns: 1fr;
        }

        .pw-docs-home h1 {
          font-size: 1.95rem;
        }
      }
    `,
  },
};

export default config;
